import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { QuoteResponse } from "~/shared/schemas/quote-schema.ts";
import { quoteResponseSchema } from "~/shared/schemas/quote-schema.ts";
import type {
	ShipmentDetailsData,
	ShipmentManagement,
} from "~/shared/schemas/shipment-management-schema.ts";
import { ShipmentUpdateResponseSchema } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	patchAndValidate,
	postAndValidate,
} from "~/shared/services/api/fetch-client.ts";

export const rerateQueryKey = (quoteId: number) =>
	["new-rerate", quoteId] as const;

const applyShipmentLinearFeet = (
	rerateResponse: QuoteResponse,
	shipmentData: ShipmentDetailsData | null,
): QuoteResponse => {
	const overwriteLinearFeet =
		shipmentData?.freightDetails.overwriteCalculatedLinearFeet;
	const calculatedLinearFeet =
		shipmentData?.freightDetails.calculatedLinearFeet;
	if (rerateResponse.calculatedLinearFeet || calculatedLinearFeet == null) {
		return rerateResponse;
	}

	// `totalLinearFeet` is the value the summary displays; it holds the
	// overwrite when one is set, otherwise it mirrors the raw calculated value.
	const totalLinearFeet =
		overwriteLinearFeet != null && overwriteLinearFeet > 0
			? overwriteLinearFeet
			: calculatedLinearFeet;

	return {
		...rerateResponse,
		calculatedLinearFeet,
		totalLinearFeet,
	};
};

export const postNewRerate = async (
	quoteId: number,
	shipmentData: ShipmentDetailsData | null,
) => {
	const url = apiRoutes.POSTNEWRERATE.replace(":quoteId", String(quoteId));
	const body = shipmentData ?? {};
	const resp = await postAndValidate(url, body, quoteResponseSchema);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const useCreateRerate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			quoteId,
			shipmentData,
		}: {
			quoteId: number;
			shipmentData: ShipmentDetailsData | null;
		}) => postNewRerate(quoteId, shipmentData),
		onSuccess: (data, { shipmentData }) => {
			const rerateResponse = applyShipmentLinearFeet(data, shipmentData);
			queryClient.setQueryData(
				rerateQueryKey(rerateResponse.id),
				rerateResponse,
			);
		},
		retry: false,
	});
};

/**
 * Reads the rerate response that {@link useCreateRerate} seeded into the query
 * cache when the user triggered a rerate from the Shipment Management page —
 * the only entry point to the rerate page. This page never creates a rerate
 * itself, so the query stays disabled and simply subscribes to the seeded cache
 * entry (keeping it from being garbage-collected while the page is mounted).
 * `rerateResponse` is `undefined` when nothing is seeded, e.g. a manually typed
 * URL or a browser refresh.
 */
export const useSeededRerate = (quoteId: number | undefined) => {
	const { data } = useQuery({
		queryKey: ["new-rerate", quoteId],
		queryFn: (): Promise<QuoteResponse> => {
			throw new Error(
				"A rerate can only be created from the Shipment Management page.",
			);
		},
		enabled: false,
		gcTime: 5 * 60 * 1000,
		staleTime: Number.POSITIVE_INFINITY,
	});

	return { rerateResponse: data };
};

export const patchShipmentRerate = async (
	shipment: ShipmentManagement,
	requiresDocGen: boolean,
) => {
	const queryParams = new URLSearchParams({
		poNumber: String(shipment?.shipmentDetails?.additionalDetails?.poNumber),
	});
	const url = apiRoutes.PATCHSHIPMENTRERATE;
	const resp = await patchAndValidate(
		`${url}?${queryParams.toString()}`,
		{ shipment, requiresDocGen },
		ShipmentUpdateResponseSchema,
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const useUpdateShipmentRerate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			shipment,
			requiresDocGen,
		}: {
			quoteId: number;
			shipment: ShipmentManagement;
			requiresDocGen: boolean;
		}) => patchShipmentRerate(shipment, requiresDocGen),
		retry: false,
		onSuccess: (data, { shipment }) => {
			// Seed under the rerate quote's own id — that is what the rerate page
			// reads from the URL. Seeding under the original shipment.quoteId would
			// mismatch and make the page bounce straight back.
			if (data.rerateQuote) {
				const rerateResponse = applyShipmentLinearFeet(
					data.rerateQuote,
					shipment.shipmentDetails,
				);
				queryClient.setQueryData(
					rerateQueryKey(rerateResponse.id),
					rerateResponse,
				);
			}
		},
	});
};
